/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';

import {IAppStore} from 'store/reducers';
import {IPlaylist, fetchPlaylist} from 'services/on-demand.service';
import {IPortalError, transformToPortalError} from 'utility/error-handler';

/**
* ACTION TYPES
*/
export const PLAYLIST_FETCHING = 'playlist::PLAYLIST_FETCHING' as const;
export const PLAYLIST_FETCHING_DONE = 'playlist::PLAYLIST_FETCHING_DONE' as const;
export const PLAYLIST_FETCHING_ERROR = 'playlist::PLAYLIST_FETCHING_ERROR' as const;

/**
* SELECTORS
*/
export const playlistSelector = ({playlist}: IAppStore): IAppStore['playlist'] => playlist;

/**
* INTERFACES
*/
interface IRequestPlaylist {
  type: typeof PLAYLIST_FETCHING;
}
interface IReceivePlaylist {
  type: typeof PLAYLIST_FETCHING_DONE;
  payload: IPlaylist[];
}
interface IFailedPlaylist {
  type: typeof PLAYLIST_FETCHING_ERROR;
  error: null | IPortalError;
}

export type PlaylistActionsType = IRequestPlaylist | IReceivePlaylist | IFailedPlaylist;

interface IPlaylistActions {
  request: () => PlaylistActionsType;
  receive: (payload: IPlaylist[]) => PlaylistActionsType;
  failed: (error: null | IPortalError) => PlaylistActionsType;
}

/**
* ACTIONS
*/

const playlistActions: IPlaylistActions = {
  request: () => ({type: PLAYLIST_FETCHING}),
  receive: payload => ({
    type: PLAYLIST_FETCHING_DONE,
    payload
  }),
  failed: error => ({
    type: PLAYLIST_FETCHING_ERROR,
    error
  })
};

export const getPlaylist = (streamId: string) => async(dispatch: Dispatch<PlaylistActionsType>): Promise<void> => {
  const {
    request,
    receive,
    failed
  } = playlistActions;

  dispatch(request());

  try {
    const playlist = await fetchPlaylist(streamId);

    dispatch(receive(playlist));
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

export const clearPlaylists = () => async(dispatch: Dispatch<PlaylistActionsType>): Promise<void> => {
  const {receive, failed} = playlistActions;

  try {
    dispatch(receive([]));
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

export type onDemandActionsType = PlaylistActionsType;